<script setup>
  import {computed, ref} from "vue";

  import ComponentRecordPrintPdfDialog from "@components/Dialogs/RecordPrintPdfDialog.vue";
  import ComponentRecordSendMailDialog from "@components/Dialogs/RecordSendMailDialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentSpotlight from "@components/Spotlight/Spotlight.vue";
  import ComponentRecordProgress from "@pages/Records/Components/RecordProgress.vue";
  import ComponentRecordStatus from "@pages/Records/Components/RecordStatus.vue";
  import RecordComponentEmailHasSentDialog from "@pages/Records/Components/Result/RecordEmailHasSentDialog.vue";
  import {sharedrecordStatus} from "@pages/Records/Enums/Enums.js";
  import {MedButton, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PagesRecordResultNextSteps",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
    transfer: {
      type: Object,
      default: null,
    },
    sharedata: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["open:shareDialog", "open:shareStatusDialog", "open:recordCaseCloseDialog"]);

  const refSendMailDialog = ref(null);
  const refPrintPdfDialog = ref(null);
  const refRecordProgress = ref(null);
  const refEmailHasSentDialog = ref(null);

  const hasAnyPosDocument = computed(() => {
    return (
      props.transfer?.has_pos_documents?.doctor_pdf ||
      props.transfer?.has_pos_documents?.patient_pdf ||
      props.transfer?.has_pos_documents?.full_pdf
    );
  });

  function openSendMailDialog() {
    refSendMailDialog.value.open();
  }

  function openPrintPdfDialog() {
    refPrintPdfDialog.value.open();
  }

  function openShareStatusDialog() {
    emit("open:shareStatusDialog");
  }

  function openShareDialog(initialTab) {
    emit("open:shareDialog", initialTab);
  }

  function openRecordCaseCloseDialog() {
    emit("open:recordCaseCloseDialog");
  }

  function triggerRecordProgressEasyCom() {
    refRecordProgress.value.easyCom();
  }

  const openEmailHasSentDialog = ({email}) => {
    refEmailHasSentDialog.value.open(email);
  };
</script>

<template>
  <section :data-test="getTestId('next-steps')">
    <h2
      v-if="$page.props.client.license.is_pro"
      class="flex items-center justify-center space-x-4 border-b-1 border-gray-200 pb-6 text-2xl font-semibold"
    >
      <ComponentIcon class="text-2xl">more_vert</ComponentIcon>
      <span>Nächste Schritte</span>
    </h2>

    <!-- "show" instead of "if" is essential here -->
    <div v-show="$page.props.client.license.is_pro" class="mt-6 flex divide-x-2 border-b-2 pb-6">
      <div class="w-full p-4">
        <h3 class="text-center font-bold">Analyseergebnis</h3>

        <div class="mt-4 flex items-start justify-center space-x-6">
          <div v-if="record.can_create_record_email" class="flex w-1/3 flex-col items-center text-center">
            <MedTooltip content="Ergebnis mit E-Mail an Arzt oder Patienten senden">
              <MedButton
                icon="email"
                variant="primary"
                rounded="full"
                size="lg"
                :data-test="getTestId('next-steps-send-email-button')"
                @click="openSendMailDialog"
              />
            </MedTooltip>
            <div class="mt-2">per E-Mail senden</div>
          </div>

          <div class="flex w-1/3 flex-col items-center text-center">
            <MedTooltip content="PDF erstellen, speichern oder drucken">
              <MedButton
                icon="print"
                variant="primary"
                rounded="full"
                size="lg"
                :data-test="getTestId('next-steps-print-pdf-button')"
                @click="openPrintPdfDialog()"
              />
            </MedTooltip>
            <div class="mt-2">PDF / drucken</div>
          </div>

          <div class="w-1/3 text-center" :data-test="getTestId('next-steps-documentation')">
            <ComponentRecordProgress
              ref="refRecordProgress"
              :record="record"
              :transfer="transfer"
              :show-easy-com-only-button="true"
              @open:record-case-close-dialog="openRecordCaseCloseDialog"
            />
            <div class="mt-2">Dokumentation</div>
          </div>
        </div>
      </div>

      <div class="w-full p-4">
        <h3 class="text-center font-bold">Fallfreigabe</h3>

        <div class="mt-4 flex items-start justify-center space-x-6">
          <div
            v-if="
              sharedata.shared_with_me &&
              sharedata.shared_owners.length > 0 &&
              sharedata.shared_owners[0].status !== sharedrecordStatus.revoked
            "
            class="flex w-1/3 flex-col items-center text-center"
          >
            <MedTooltip content="Bearbeitung abschließen und an Absender zurücksenden">
              <MedButton
                icon="check"
                variant="primary"
                rounded="full"
                size="lg"
                :data-test="getTestId('next-steps-share-and-back-button')"
                @click="openShareStatusDialog"
              />
            </MedTooltip>
            <div class="mt-2">Zurücksenden</div>
          </div>

          <div
            v-if="
              (sharedata.shared_with_me && $page.props.can.allow_shared_document_forwarding) ||
              !sharedata.shared_with_me
            "
            class="flex w-1/3 flex-col items-center text-center"
          >
            <ComponentSpotlight
              :suppressed="!$page.props.can.allow_expert_service"
              storage-key="Records/NextSteps:suppress-expert-service-spotlight"
              :storage-identifier="record.id"
              label="Mit Kollegen oder Ärzteservice teilen"
            >
              <MedTooltip v-if="sharedata.shared_by_me" content="Variante teilen (wurde bereits geteilt)">
                <MedButton
                  icon="share"
                  variant="primary"
                  rounded="full"
                  size="lg"
                  :data-test="getTestId('next-steps-share-again-button')"
                  @click="openShareDialog()"
                />
              </MedTooltip>

              <MedTooltip v-else content="Variante teilen (wurde bislang noch nicht geteilt)">
                <MedButton
                  flat
                  icon="share"
                  rounded="full"
                  size="lg"
                  class="border-2 border-mcred"
                  :data-test="getTestId('next-steps-share-button')"
                  @click="openShareDialog()"
                />
              </MedTooltip>
            </ComponentSpotlight>
            <div class="mt-2">Variante teilen</div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col items-center p-4">
      <template v-if="record.is_pos_record">
        <MedButton
          :variant="!hasAnyPosDocument ? 'primary' : 'secondary'"
          :data-test="getTestId('next-steps-open-easy-com-button')"
          @click="triggerRecordProgressEasyCom()"
        >
          <template v-if="hasAnyPosDocument">Dokumente gespeichert</template>

          <template v-else>Weiter</template>
        </MedButton>
      </template>

      <template v-else>
        <h3 class="font-bold">Status der Variante</h3>
        <component-record-status :record="record" class="mt-2" inner-class="bottom-8 min-w-24 max-w-32" />
      </template>
    </div>
  </section>

  <ComponentRecordSendMailDialog
    ref="refSendMailDialog"
    :patient="patient"
    :record="record"
    :test-id="getTestId('next-steps')"
    @email-sent="openEmailHasSentDialog"
  />

  <ComponentRecordPrintPdfDialog
    ref="refPrintPdfDialog"
    :patient="patient"
    :record="record"
    :test-id="getTestId('next-steps')"
  />

  <RecordComponentEmailHasSentDialog ref="refEmailHasSentDialog" :test-id="getTestId('next-steps')" />
</template>
